/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Chart scroll regions must be focusable for keyboard scrolling. */
import { type FC, type ReactNode, useId, useMemo } from "react";

import { css, cx } from "@emotion/css";
import { useElementSize } from "@mantine/hooks";
import { BarChart, Grid, Threshold, Tooltip, XAxis, YAxis } from "@snapchat/snap-design-system-charts";

import { formatNumbers } from "../../../lens-stats/formatters";
import { convertChartLabelNumber } from "../../converters";
import { formatTooltipContent, formatTooltipTitle } from "../../formatters";
import { ChartDimensions, type InsightsMetrics, type StatsItem } from "../../models";

import { UTCNote, chartContainer, suppressedBars } from "./charts.styles";
import { UtcNoteMessage } from "./messages.generated";

const scrollViewport = css`
    overflow-x: auto;
    min-width: 0;
`;

type ChartItem = StatsItem & { suppressed?: boolean };

interface BarChartComponentProps {
    data: InsightsMetrics<StatsItem>;
    contentName: ReactNode;
    yLabelConverter?: (label: string) => string;
    formatter?: (value: number | string) => string;
    minimumVisibleValue?: number;
    yAxisWidth?: number;
    responsive?: boolean;
    scrollable?: boolean;
    latestBarPending?: boolean;
}

export const BarChartComponent: FC<BarChartComponentProps> = ({
    data,
    contentName,
    minimumVisibleValue,
    yAxisWidth,
    responsive = false,
    scrollable = false,
    latestBarPending = true,
    formatter = formatNumbers,
    yLabelConverter = convertChartLabelNumber,
}) => {
    const labelId = useId();
    const { ref, width } = useElementSize();
    const formatContent = useMemo(() => {
        const exact = formatTooltipContent(contentName, formatter);
        const belowMinimum = formatTooltipContent(contentName, (value) => `<${formatter(value)}`);
        return (item: ChartItem) => (item.suppressed ? belowMinimum : exact)(item);
    }, [contentName, formatter]);
    const items: ChartItem[] = data.stats.map((item) =>
        minimumVisibleValue && item.value > 0 && item.value < minimumVisibleValue
            ? { ...item, value: minimumVisibleValue, suppressed: true }
            : item,
    );
    const suppressedIndices = items.flatMap((item, index) => (item.suppressed ? [index] : []));

    return (
        <div
            className={cx(chartContainer, scrollable && scrollViewport, suppressedBars(suppressedIndices))}
            data-latest-bar-pending={latestBarPending}
            ref={responsive ? ref : undefined}
            role={scrollable ? "region" : undefined}
            aria-labelledby={scrollable ? labelId : undefined}
            tabIndex={scrollable ? 0 : undefined}
        >
            {scrollable && (
                <span id={labelId} hidden>
                    {contentName}
                </span>
            )}
            <BarChart
                data={items}
                width={scrollable ? Math.max(width, data.stats.length * 40 + 80) : responsive ? width : 1040}
                height={250}
                x={ChartDimensions.key}
                y={ChartDimensions.value}
            >
                <Grid strokeDasharray="1 6" />
                <XAxis labelOrientation="vertical" interval={responsive && !scrollable ? "preserveStartEnd" : 0} />
                <YAxis label={yLabelConverter} width={yAxisWidth} />
                <Tooltip title={formatTooltipTitle} content={formatContent} />
                {minimumVisibleValue === undefined && <Threshold value={data.averageValue} />}
            </BarChart>
            <UTCNote>
                <UtcNoteMessage />
            </UTCNote>
        </div>
    );
};

/* eslint-enable jsx-a11y/no-noninteractive-tabindex -- End of keyboard-scroll viewport component. */
